"use client";

import { WALLET_LABELS } from "@nexus-cross/crossx-design-system";
import * as React from "react";
import "../styles/theme.css";
import "../styles/component.css";
import { WalletInfo } from "../../wallet-info/components/WalletInfo";
import type { ConnectButtonProps, WalletProvider } from "../types";
import {
  APPLE_ICON,
  BINANCE_ICON,
  CROSSX_ICON,
  GOOGLE_ICON,
  METAMASK_ICON,
} from "../icons";
import {
  TrackingBoundary,
  setDappUiAnalyticsUser,
} from "../../../lib/sentry/index";

/**
 * 3-state wallet connect button. Caller drives the visual state:
 *
 *   - `isConnecting === true`        → 스피너 disabled 버튼
 *   - `address` 있음                 → WalletInfo + connected pill
 *   - `address`가 있는데 `provider` 없음 → SDK 조회 전이 상태, placeholder 원
 *   - 둘 다 없음                     → "Connect Wallet" 버튼
 *
 * view-only: wagmi / connect-kit 같은 web3 의존성은 갖지 않으며 모든
 * 상태/데이터/콜백은 props로 주입받는다. 실제 wagmi 연결 로직은
 * `@nexus-cross/connect-kit-react`의 상위 래퍼에서 수행한다.
 */
export function ConnectButton({
  isConnecting,
  address,
  provider,
  providerName,
  accountName,
  sendAccounts,
  onConnect,
  onDisconnect,
  onCopy,
  onSelectWallet,
  onBuy,
  onBuyDisabledMessage,
  label = "Connect Wallet",
  connectingLabel = "Connecting...",
  disconnectLabel,
  className,
  theme = "dark",
  env,
  showBalance = true,
  showPortfolio,
  drawerDirection,
  modal,
  connectorId,
  style,
  walletInfoStyle,
  sendTransaction,
  getTransactionReceipt,
  estimateGas,
  onReceive,
  onBridge,
  onSend,
  bridgeTokens,
  bridgeHistory,
  getBridgeQuote,
  getBridgeToTokens,
  getBridgeApproval,
  approveBridge,
  submitBridge,
}: ConnectButtonProps) {
  const buttonClass = className ? `cb-button ${className}` : "cb-button";
  const pillClass = className ? `cb-pill ${className}` : "cb-pill";

  React.useEffect(() => {
    setDappUiAnalyticsUser(address);
  }, [address]);

  if (isConnecting) {
    return (
      <button
        type="button"
        disabled
        className={buttonClass}
        style={style}
        aria-busy="true"
      >
        <span className="cb-spinner" aria-hidden="true" />
        <span className="cb-button-label">{connectingLabel}</span>
      </button>
    );
  }

  if (address) {
    const resolvedProviderName =
      providerName ?? getDefaultProviderName(provider);
    const iconUrl = provider ? PROVIDER_ICON[provider] : undefined;

    return (
      <TrackingBoundary feature="connect_button">
        <WalletInfo
          env={env}
          theme={theme}
          drawerDirection={drawerDirection}
          modal={modal}
          showBalance={showBalance}
          showPortfolio={showPortfolio}
          walletAddress={address}
          accountName={accountName}
          sendAccounts={sendAccounts}
          // 계정 라벨이 없을 때 WalletInfo 가 쓰는 폴백. `getDefaultProviderName`
          // 의 "Wallet" placeholder 는 의도적으로 넘기지 않는다 — provider 를
          // 모르는 상태를 계정 라벨로 승격시키면 안 된다.
          connectorName={
            providerName ?? (provider ? PROVIDER_NAME[provider] : undefined)
          }
          connectorId={connectorId}
          onSelectWallet={onSelectWallet}
          onCopyAddress={
            onCopy
              ? (_: string, success: boolean) => {
                  if (success) onCopy();
                }
              : undefined
          }
          onDisconnect={onDisconnect}
          disconnectLabel={disconnectLabel}
          style={walletInfoStyle}
          sendTransaction={sendTransaction}
          getTransactionReceipt={getTransactionReceipt}
          estimateGas={estimateGas}
          onReceive={onReceive}
          onBridge={onBridge}
          onBuy={onBuy}
          onBuyDisabledMessage={onBuyDisabledMessage}
          onSend={onSend}
          bridgeTokens={bridgeTokens}
          bridgeHistory={bridgeHistory}
          getBridgeQuote={getBridgeQuote}
          getBridgeToTokens={getBridgeToTokens}
          getBridgeApproval={getBridgeApproval}
          approveBridge={approveBridge}
          submitBridge={submitBridge}
        >
          <WalletInfo.Trigger asChild>
            <button
              type="button"
              className={pillClass}
              data-track="wallet_pill_open"
              style={style}
              aria-label={`Connected with ${resolvedProviderName}: ${shorten(address)}`}
            >
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt=""
                  width={18}
                  height={18}
                  className="cb-pill-icon"
                  data-provider={provider}
                />
              ) : (
                <span className="cb-pill-icon-placeholder" aria-hidden="true" />
              )}
              <span className="cb-pill-address">{shorten(address)}</span>
            </button>
          </WalletInfo.Trigger>
          <WalletInfo.Content align="end" sideOffset={8} />
        </WalletInfo>
      </TrackingBoundary>
    );
  }

  return (
    <TrackingBoundary feature="connect_button">
      <button
        type="button"
        onClick={onConnect}
        className={buttonClass}
        data-track="connect_click"
        style={style}
        aria-label={typeof label === "string" ? label : "Connect Wallet"}
      >
        <ConnectWalletIcon />
        <span className="cb-button-label">{label}</span>
      </button>
    </TrackingBoundary>
  );
}

function ConnectWalletIcon() {
  return (
    <svg
      className="cb-button-icon"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3.75 5.25H14.25C15.0784 5.25 15.75 5.92157 15.75 6.75V13.5C15.75 14.3284 15.0784 15 14.25 15H3.75C2.92157 15 2.25 14.3284 2.25 13.5V4.5C2.25 3.67157 2.92157 3 3.75 3H13.5"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 9.75H15.75V12.75H12C11.1716 12.75 10.5 12.0784 10.5 11.25C10.5 10.4216 11.1716 9.75 12 9.75Z"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.75 11.25H12.7575"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

const PROVIDER_ICON: Record<WalletProvider, string> = {
  google: GOOGLE_ICON,
  apple: APPLE_ICON,
  cross: CROSSX_ICON,
  metamask: METAMASK_ICON,
  binance: BINANCE_ICON,
};

const PROVIDER_NAME: Record<WalletProvider, string> = {
  google: "Google",
  apple: "Apple",
  cross: WALLET_LABELS.default,
  metamask: "MetaMask",
  binance: "Binance Wallet",
};

function getDefaultProviderName(provider: WalletProvider | undefined): string {
  if (!provider) return "Wallet";
  return PROVIDER_NAME[provider];
}

function shorten(address: string): string {
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}
